/* eslint-disable no-restricted-globals */

import { StorageKeys, StorageType } from "@/types/web-storage"

import { isClientSide } from "@/lib/is-client-side"

export interface StorageOptions {
  type?: StorageType
  noInMemoryFallback?: boolean // If true, the requested browser storage backend must complete or throw WebStorageUnavailableError
}

export class WebStorageUnavailableError extends Error {
  readonly storageType: StorageType

  constructor(storageType: StorageType, cause?: unknown) {
    super(`${storageType} storage is unavailable`, { cause })
    this.name = "WebStorageUnavailableError"
    this.storageType = storageType
  }
}

type SelectedStorage =
  | { backend: "local"; storage: Storage }
  | { backend: "session"; storage: Storage }
  | { backend: "memory"; storage: Map<string, string> }

class WebStorage {
  // These fallbacks only last until the page reloads. They retain optional writes
  // when browser storage is unavailable or rejects a write because it is full.
  private localStorageInMemoryFallback: Map<string, string> = new Map()
  private sessionStorageInMemoryFallback: Map<string, string> = new Map()
  private localStorageAvailable: boolean | null = null
  private sessionStorageAvailable: boolean | null = null
  private localStorageError: unknown
  private sessionStorageError: unknown

  private getLocalStorage(): Storage | null {
    if (!isClientSide() || this.localStorageAvailable === false) return null

    try {
      const storage = localStorage
      if (this.localStorageAvailable === null) {
        storage.setItem("__localStorage_test__", "__localStorage_test__")
        storage.removeItem("__localStorage_test__")
      }
      this.localStorageAvailable = true
      return storage
    } catch (error) {
      this.localStorageAvailable = false
      this.localStorageError = error
      return null
    }
  }

  private getSessionStorage(): Storage | null {
    if (!isClientSide() || this.sessionStorageAvailable === false) return null

    try {
      const storage = sessionStorage
      if (this.sessionStorageAvailable === null) {
        storage.setItem("__sessionStorage_test__", "__sessionStorage_test__")
        storage.removeItem("__sessionStorage_test__")
      }
      this.sessionStorageAvailable = true
      return storage
    } catch (error) {
      this.sessionStorageAvailable = false
      this.sessionStorageError = error
      return null
    }
  }

  private getStorage(
    type: StorageType,
    noInMemoryFallback: boolean
  ): SelectedStorage | null {
    if (!isClientSide()) return null

    if (type === "local") {
      const localStorage = this.getLocalStorage()
      if (localStorage) return { backend: "local", storage: localStorage }
      if (noInMemoryFallback) return null

      const sessionStorage = this.getSessionStorage()
      if (sessionStorage) return { backend: "session", storage: sessionStorage }
    } else {
      const sessionStorage = this.getSessionStorage()
      if (sessionStorage) return { backend: "session", storage: sessionStorage }
      if (noInMemoryFallback) return null
    }

    const inMemoryFallback =
      type === "local"
        ? this.localStorageInMemoryFallback
        : this.sessionStorageInMemoryFallback
    return noInMemoryFallback
      ? null
      : { backend: "memory", storage: inMemoryFallback }
  }

  private inMemoryFallback(type: StorageType): Map<string, string> {
    return type === "local"
      ? this.localStorageInMemoryFallback
      : this.sessionStorageInMemoryFallback
  }

  private unavailableError(
    storageType: StorageType
  ): WebStorageUnavailableError {
    const cause =
      storageType === "local"
        ? this.localStorageError
        : this.sessionStorageError
    return new WebStorageUnavailableError(storageType, cause)
  }

  getItem(key: StorageKeys, inOptions?: StorageOptions): string | null {
    const storageType = inOptions?.type ?? "local"
    const noInMemoryFallback = inOptions?.noInMemoryFallback ?? false
    const selectedStorage = this.getStorage(storageType, noInMemoryFallback)
    if (!selectedStorage) {
      if (noInMemoryFallback) throw this.unavailableError(storageType)
      return null
    }

    if (!noInMemoryFallback) {
      const memoryValue = this.inMemoryFallback(storageType).get(key)
      if (memoryValue !== undefined) return memoryValue
    }

    if (selectedStorage.backend === "memory") return null

    try {
      return selectedStorage.storage.getItem(key)
    } catch (error) {
      if (noInMemoryFallback) {
        throw new WebStorageUnavailableError(storageType, error)
      }
      return null
    }
  }

  setItem(key: StorageKeys, value: string, inOptions?: StorageOptions): void {
    const storageType = inOptions?.type ?? "local"
    const noInMemoryFallback = inOptions?.noInMemoryFallback ?? false
    const selectedStorage = this.getStorage(storageType, noInMemoryFallback)
    if (!selectedStorage) {
      if (noInMemoryFallback) throw this.unavailableError(storageType)
      return
    }

    if (selectedStorage.backend === "memory") {
      selectedStorage.storage.set(key, value)
      return
    }

    try {
      selectedStorage.storage.setItem(key, value)
      this.inMemoryFallback(storageType).delete(key)
    } catch (error) {
      if (noInMemoryFallback) {
        throw new WebStorageUnavailableError(storageType, error)
      }
      if (error instanceof Error && error.name === "QuotaExceededError") {
        this.inMemoryFallback(storageType).set(key, value)
      }
    }
  }

  removeItem(key: StorageKeys, inOptions?: StorageOptions): void {
    const storageType = inOptions?.type ?? "local"
    const noInMemoryFallback = inOptions?.noInMemoryFallback ?? false
    const selectedStorage = this.getStorage(storageType, noInMemoryFallback)
    if (!selectedStorage) {
      if (noInMemoryFallback) throw this.unavailableError(storageType)
      return
    }

    if (!noInMemoryFallback) this.inMemoryFallback(storageType).delete(key)
    if (selectedStorage.backend === "memory") return

    try {
      selectedStorage.storage.removeItem(key)
      this.inMemoryFallback(storageType).delete(key)
    } catch (error) {
      if (noInMemoryFallback) {
        throw new WebStorageUnavailableError(storageType, error)
      }
    }
  }

  clear(inOptions?: StorageOptions): void {
    const storageType = inOptions?.type ?? "local"
    const noInMemoryFallback = inOptions?.noInMemoryFallback ?? false
    const selectedStorage = this.getStorage(storageType, noInMemoryFallback)
    if (!selectedStorage) {
      if (noInMemoryFallback) throw this.unavailableError(storageType)
      return
    }

    if (!noInMemoryFallback) this.inMemoryFallback(storageType).clear()
    if (selectedStorage.backend === "memory") return

    try {
      selectedStorage.storage.clear()
      this.inMemoryFallback(storageType).clear()
    } catch (error) {
      if (noInMemoryFallback) {
        throw new WebStorageUnavailableError(storageType, error)
      }
    }
  }

  getJSON<T = unknown>(key: StorageKeys, inOptions?: StorageOptions): T {
    try {
      const item = this.getItem(key, inOptions)
      return JSON.parse(item ?? "")
    } catch (error) {
      if (error instanceof WebStorageUnavailableError) throw error

      try {
        this.removeItem(key, inOptions)
      } catch {
        // Preserve the JSON parsing failure when storage cleanup fails.
      }
      throw error
    }
  }

  tryGetJSON<T = unknown>(
    key: StorageKeys,
    inOptions: StorageOptions & { defaultValue: T }
  ): T
  tryGetJSON<T = unknown>(
    key: StorageKeys,
    inOptions?: StorageOptions & { defaultValue?: T }
  ): T | null
  tryGetJSON<T = unknown>(
    key: StorageKeys,
    inOptions?: StorageOptions & { defaultValue?: T }
  ): T | null {
    try {
      return this.getJSON<T>(key, inOptions)
    } catch (error) {
      if (error instanceof WebStorageUnavailableError) throw error
      if (inOptions?.defaultValue) {
        return inOptions.defaultValue
      }
      return null
    }
  }

  setJSON<T = unknown>(
    key: StorageKeys,
    value: T,
    inOptions?: StorageOptions
  ): void {
    const json = JSON.stringify(value)
    this.setItem(key, json, inOptions)
  }

  // Test helper - only use in tests to reset storage availability cache
  // This is needed because we use a singleton instance
  __resetForTesting(): void {
    this.localStorageAvailable = null
    this.sessionStorageAvailable = null
    this.localStorageError = undefined
    this.sessionStorageError = undefined
    this.localStorageInMemoryFallback.clear()
    this.sessionStorageInMemoryFallback.clear()
  }
}

export const webStorage = new WebStorage()
